import { useTranslation } from 'react-i18next';

import { ModalsProvider } from '@speeki/global-ui-components';
import { useModal } from '@speeki/react-hooks';

import { SubsidiarySiteFromUrlDocument } from '@graphql/generated/graphql';

import {
    countriesOptions,
    defaultSelectOption,
    relationshipOptions,
} from '@utils/dictionary';
import { useUserCompany } from '@utils/hooks/useUserCompany';
import { useUserOptions } from '@utils/hooks/useUserOptions';

import { AuthService } from '@services/AuthService/AuthService';

import { useQuery, useReactiveVar } from '@apollo/client';
import {
    authVar,
    subsidiarySiteUuidFromUrlVar,
    tenantModulesVar,
} from '@boot/reactiveVariables';
import { Option } from '@domains/settings/atoms/Option/Option';
import { CountryChangeModal } from '@domains/settings/modals/CountryChangeModal/CountryChangeModal';
import { LanguageChangeModal } from '@domains/settings/modals/LanguageChangeModal/LanguageChangeModal';
import { MfaEmailModal } from '@domains/settings/modals/MfaEmailModal/MfaEmailModal';
import { PasswordChangeModal } from '@domains/settings/modals/PasswordChangeModal/PasswordChangeModal';
import { RelationshipChangeModal } from '@domains/settings/modals/RelationshipChangeModal/RelationshipChangeModal';
import { SettingsCard } from '@domains/settings/pages/SettingsPage/SettingsPage.styled';

export const UserOptions = () => {
    const { t } = useTranslation();

    const { data: userCompany } = useUserCompany();
    const { data: userOptions } = useUserOptions();

    const auth = useReactiveVar(authVar);
    const isGuest = auth.isGuest;
    const isLoggedIn = auth.isLoggedIn;
    const tenantUuid = AuthService.getTenantUuid();
    const isSubsidiarySiteEnabled =
        useReactiveVar(tenantModulesVar).subsidiarySites;
    const subsidiarySiteUuidFromUrl = useReactiveVar(
        subsidiarySiteUuidFromUrlVar,
    );

    const { data } = useQuery(SubsidiarySiteFromUrlDocument, {
        fetchPolicy: 'cache-and-network',
        skip: !isSubsidiarySiteEnabled,
        variables: {
            input: {
                subsidiaryUuid: subsidiarySiteUuidFromUrl || null,
                tenantUuid: tenantUuid || '',
            },
            tenantUuid: isLoggedIn ? undefined : tenantUuid,
        },
    });
    const currentSubsidiary = data?.subsidiarySite;
    const isHq = currentSubsidiary?.isHeadquarters ?? false;

    const countryChangeModal = useModal();
    const languageChangeModal = useModal();
    const passwordChangeModal = useModal();
    const relationshipChangeModal = useModal();
    const mfaEmailChangeModal = useModal();

    const currentCountry = defaultSelectOption(
        countriesOptions(),
        userOptions?.user.country,
    )?.label;
    const currentRelationship = defaultSelectOption(
        relationshipOptions(),
        userOptions?.user.relationship,
    )?.label;

    return (
        <>
            <SettingsCard>
                <Option
                    actionName={userCompany?.user.tenant?.name}
                    icon={'SvgCompany'}
                    onPress={() => ModalsProvider.open('CompanyChangeModal')}
                    optionName={t('COMPANY')}
                />

                {isSubsidiarySiteEnabled && (
                    <Option
                        actionName={
                            (isHq
                                ? currentSubsidiary?.reportingLabel
                                : currentSubsidiary?.name) || ''
                        }
                        icon={'SvgBriefcase'}
                        onPress={() => ModalsProvider.open('EntityChangeModal')}
                        optionName={t('SUBSIDIARY')}
                    />
                )}

                <Option
                    actionName={currentRelationship}
                    icon={'SvgRelationship'}
                    onPress={() => relationshipChangeModal.setOpen(true)}
                    optionName={t('RELATIONSHIP')}
                />
                {!isGuest && (
                    <Option
                        actionName={t('CHANGE_PASSWORD')}
                        icon={'SvgPassword'}
                        onPress={() => passwordChangeModal.setOpen(true)}
                        optionName={t('PASSWORD')}
                    />
                )}
                {!isGuest && userOptions?.user.recoveryEmail && (
                    <Option
                        actionName={userOptions?.user.recoveryEmail}
                        icon={'SvgKey'}
                        onPress={() => mfaEmailChangeModal.setOpen(true)}
                        optionName={t('MULTI_FACTOR_AUTH')}
                    />
                )}

                {isGuest && (
                    <>
                        <Option
                            actionName={currentCountry}
                            icon={'SvgCountry'}
                            onPress={() => countryChangeModal.setOpen(true)}
                            optionName={t('COUNTRY')}
                        />
                        <Option
                            actionName={
                                userOptions?.user?.languageSelect?.label
                            }
                            icon={'SvgLanguage'}
                            onPress={() => languageChangeModal.setOpen(true)}
                            optionName={t('LANGUAGE')}
                        />
                    </>
                )}
            </SettingsCard>
            {!isGuest && (
                <SettingsCard>
                    <Option
                        actionName={currentCountry}
                        icon={'SvgCountry'}
                        onPress={() => countryChangeModal.setOpen(true)}
                        optionName={t('COUNTRY')}
                    />
                    <Option
                        actionName={userOptions?.user?.languageSelect?.label}
                        icon={'SvgLanguage'}
                        onPress={() => languageChangeModal.setOpen(true)}
                        optionName={t('LANGUAGE')}
                    />
                </SettingsCard>
            )}

            {/* // Modals */}
            {countryChangeModal.open && (
                <CountryChangeModal
                    onClose={() => countryChangeModal.setOpen(false)}
                    open={countryChangeModal.open}
                />
            )}
            {languageChangeModal.open && (
                <LanguageChangeModal
                    onClose={() => languageChangeModal.setOpen(false)}
                    open={languageChangeModal.open}
                />
            )}
            {passwordChangeModal.open && (
                <PasswordChangeModal
                    onClose={() => passwordChangeModal.setOpen(false)}
                    open={passwordChangeModal.open}
                />
            )}
            {relationshipChangeModal.open && (
                <RelationshipChangeModal
                    onClose={() => relationshipChangeModal.setOpen(false)}
                    open={relationshipChangeModal.open}
                />
            )}
            {mfaEmailChangeModal.open && (
                <MfaEmailModal
                    onClose={() => mfaEmailChangeModal.setOpen(false)}
                    open={mfaEmailChangeModal.open}
                />
            )}
        </>
    );
};
